import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { safeConfirm } from '../../lib/confirm';
import { toast } from 'sonner';
import { Eye, EyeOff, Upload, FileText, Trash2, ClipboardList, UserX, FolderOpen, Search, Activity, RotateCcw, Clock, MailWarning } from 'lucide-react';
import { idLabelFor } from '../../lib/idLabels';

export default function AdminPatients() {
  const { user } = useAuth();
  const canDelete = user?.role === 'admin';
  const [patients, setPatients] = useState([]);
  const [reveal, setReveal] = useState({});
  const [selected, setSelected] = useState(null);
  const [docs, setDocs] = useState([]);
  const [docFile, setDocFile] = useState(null);
  const [docDesc, setDocDesc] = useState('');
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [recentlyDeleted, setRecentlyDeleted] = useState([]);

  const load = () => api.get('/patients').then(r => setPatients(r.data));
  const loadDeleted = () => {
    if (canDelete) api.get('/patients-recently-deleted').then(r => setRecentlyDeleted(r.data)).catch(()=>{});
  };
  useEffect(() => { load(); loadDeleted(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (selected) api.get('/documents', { params: { patient_id: selected.user_id } }).then(r => setDocs(r.data));
  }, [selected]);

  const filtered = patients.filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [p.full_name, p.email, p.patient_code, p.phone, p.pesel, p.id_number, p.city, p.country]
      .filter(Boolean).some(v => String(v).toLowerCase().includes(q));
  });

  const upload = async () => {
    if (!docFile || !selected) return;
    setLoading(true);
    const fd = new FormData();
    fd.append('patient_id', selected.user_id);
    fd.append('category', 'medical');
    fd.append('description', docDesc);
    fd.append('file', docFile);
    try {
      await api.post('/documents/upload', fd);
      setDocFile(null); setDocDesc('');
      const r = await api.get('/documents', { params: { patient_id: selected.user_id } });
      setDocs(r.data);
    } finally { setLoading(false); }
  };

  const removeDoc = async (id) => {
    if (!(await safeConfirm('Delete this document?'))) return;
    await api.delete(`/documents/${id}`);
    setDocs(docs.filter(d => d.document_id !== id));
  };

  const deletePatient = async (p) => {
    if (!(await safeConfirm(`Delete ${p.full_name}? Their account is blocked immediately and moves to "Recently deleted" — you can restore it any time within 30 days, after which it is permanently erased.`))) return;
    try {
      await api.delete(`/patients/${p.user_id}`);
      toast.success(`${p.full_name} moved to Recently deleted — 30-day undo available below.`);
      if (selected?.user_id === p.user_id) setSelected(null);
      load(); loadDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed.');
    }
  };

  const restorePatient = async (r) => {
    try {
      await api.post(`/patients/${r.user_id}/restore`);
      toast.success(`${r.name || r.email} restored — account active again.`);
      load(); loadDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Restore failed.');
    }
  };

  const hardDeletePatient = async (r) => {
    if (!(await safeConfirm(`PERMANENTLY erase ${r.name || r.email} right now? This removes their account, files, appointments and invoices immediately (e.g. for a GDPR erasure request). This cannot be undone.`))) return;
    try {
      await api.delete(`/patients/${r.user_id}?hard=true`);
      toast.success('Patient permanently erased.');
      loadDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erase failed.');
    }
  };

  return (
    <div className="p-8 max-w-7xl">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">RECORDS</div>
        <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">Patients</h1>
        <p className="text-fg-muted mt-2">All names and ID numbers are masked. Click the eye icon to reveal protected data.</p>
      </div>

      <div className="mb-4 relative max-w-md">
        <Search className="w-4 h-4 text-fg-muted absolute left-3 top-1/2 -translate-y-1/2"/>
        <input
          className="input pl-10"
          placeholder="Search by name, email, patient code, phone…"
          value={search}
          onChange={e=>setSearch(e.target.value)}
          data-testid="patient-search"
        />
      </div>

      <div className={selected ? 'grid lg:grid-cols-3 gap-4' : 'grid gap-4'}>
        <div className={selected ? 'lg:col-span-2 admin-card' : 'admin-card'}>
          <table>
            <thead>
              <tr><th>Name</th><th>Email</th><th>Country</th><th>ID #</th><th>Phone</th><th></th></tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.user_id} data-testid={`patient-row-${p.user_id}`}>
                  <td>
                    <button onClick={() => setReveal({ ...reveal, [p.user_id]: !reveal[p.user_id] })}
                            className="inline-flex items-center gap-2"
                            data-testid={`reveal-${p.user_id}`}>
                      {reveal[p.user_id] ? <Eye className="w-3.5 h-3.5 text-fg-muted" /> : <EyeOff className="w-3.5 h-3.5 text-fg-muted" />}
                      <span className={reveal[p.user_id] ? '' : 'pii-blur'}>{p.full_name}</span>
                    </button>
                  </td>
                  <td className="text-xs text-fg-muted">
                    {p.email}
                    {p.email_undeliverable && (
                      <span className="ml-1.5 inline-flex items-center gap-1 text-[10px] font-medium text-[#B83A3A] bg-[#B83A3A]/10 px-1.5 py-0.5 rounded-full whitespace-nowrap align-middle"
                            title={`Email bounced — ${p.email_bounce_reason || 'undeliverable'}. Confirm appointments by SMS.`}
                            data-testid={`email-undeliverable-${p.user_id}`}>
                        <MailWarning className="w-3 h-3"/> undeliverable
                      </span>
                    )}
                  </td>
                  <td><span className="badge-neutral">{p.country || '—'}</span></td>
                  <td className={`font-mono text-xs ${reveal[p.user_id] ? '' : 'pii-blur'}`} title={idLabelFor(p.country)}>{p.id_number || p.pesel || '—'}</td>
                  <td className="text-xs">{p.phone || '—'}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setSelected(p)} className="text-xs text-brand"
                              data-testid={`select-${p.user_id}`}>Open</button>
                      <Link to={`/admin/patients/${p.user_id}/chart`} className="text-xs text-brand inline-flex items-center gap-1"
                            data-testid={`chart-${p.user_id}`}>
                        <FolderOpen className="w-3.5 h-3.5"/> Chart
                      </Link>
                      <Link to={`/admin/patients/${p.user_id}/interview`} className="text-xs text-brand inline-flex items-center gap-1"
                            data-testid={`interview-${p.user_id}`}>
                        <ClipboardList className="w-3.5 h-3.5"/> Interview
                      </Link>
                      {canDelete && (
                      <button onClick={() => deletePatient(p)} className="text-xs text-[#B83A3A] inline-flex items-center gap-1"
                              data-testid={`delete-patient-${p.user_id}`}>
                        <UserX className="w-3.5 h-3.5"/> Delete
                      </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!filtered.length && <tr><td colSpan="6" className="text-center text-fg-muted py-8">{search ? 'No matches.' : 'No patients yet.'}</td></tr>}
            </tbody>
          </table>
        </div>

        {selected && (
        <div className="admin-card">
          <div className="flex items-center justify-between mb-1">
              <h3 className="font-heading font-semibold">{selected.full_name}</h3>
              <button onClick={()=>setSelected(null)} className="text-xs text-fg-muted hover:text-fg" data-testid="close-patient-panel">Close ×</button>
          </div>
              <div className="text-xs text-fg-muted">{selected.email}</div>
              {selected.email_undeliverable && (
                <div className="mt-1 text-[11px] text-[#B83A3A] flex items-center gap-1" data-testid="panel-email-undeliverable">
                  <MailWarning className="w-3.5 h-3.5 shrink-0"/> Email undeliverable — confirm appointments by SMS.
                </div>
              )}

              <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                <Info label="DOB" value={selected.date_of_birth || '—'} />
                <Info label="Country" value={selected.country || '—'} />
                <Info label="Insurance" value={selected.insurance_provider || '—'} />
                <Info label="GDPR" value={selected.consent_gdpr ? 'Yes' : 'No'} />
              </div>

              <div className="mt-6 border-t border-soft pt-4">
                <h4 className="font-heading font-semibold mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-brand" /> Medical documents
                </h4>
                <div className="space-y-2">
                  {docs.map(d => (
                    <div key={d.document_id} className="flex items-center justify-between text-sm">
                      <div>
                        <div className="font-medium">{d.filename}</div>
                        <div className="text-xs text-fg-muted">{d.category} · {(d.size_bytes/1024).toFixed(1)} KB</div>
                      </div>
                      <button onClick={() => removeDoc(d.document_id)} className="text-[#B83A3A]"
                              data-testid={`delete-doc-${d.document_id}`}>
                        <Trash2 className="w-4 h-4"/>
                      </button>
                    </div>
                  ))}
                  {!docs.length && <div className="text-xs text-fg-muted">No documents.</div>}
                </div>

                <div className="mt-3 space-y-2">
                  <input className="input text-sm" placeholder="Description" value={docDesc}
                         onChange={(e)=>setDocDesc(e.target.value)} data-testid="doc-desc" />
                  <input type="file" onChange={(e)=>setDocFile(e.target.files[0])} className="text-xs"
                         data-testid="doc-file" />
                  <button onClick={upload} disabled={!docFile || loading}
                          className="btn-primary w-full inline-flex items-center justify-center gap-2 text-sm"
                          data-testid="doc-upload-btn">
                    <Upload className="w-4 h-4" /> Upload
                  </button>
                </div>
                <div className="mt-3 badge-success w-fit">HIPAA / EU Compliant</div>
              </div>
        </div>
        )}
      </div>

      {/* Recently deleted — 30-day undo safety net (admin only) */}
      {canDelete && recentlyDeleted.length > 0 && (
        <div className="admin-card mt-6 border-[#C89B3C]/40" data-testid="recently-deleted-card">
          <h3 className="font-heading font-semibold flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-[#7A5500]"/> Recently deleted
            <span className="text-xs font-normal text-fg-muted">— restorable for 30 days, then erased permanently by the nightly sweep</span>
          </h3>
          <table className="mt-2">
            <thead>
              <tr><th>Patient</th><th>Deleted</th><th>Deleted by</th><th>Time left</th><th></th></tr>
            </thead>
            <tbody>
              {recentlyDeleted.map(r => (
                <tr key={r.user_id} data-testid={`deleted-row-${r.user_id}`}>
                  <td>
                    <div className="font-medium">{r.name || '—'}</div>
                    <div className="text-xs text-fg-muted">{r.email}</div>
                  </td>
                  <td className="text-xs">{(r.deletion_scheduled_at || '').slice(0, 10)}</td>
                  <td className="text-xs text-fg-muted">{r.deleted_by_name || '—'}</td>
                  <td className="text-xs">
                    <span className={`badge-neutral ${r.days_remaining <= 5 ? '!text-[#B83A3A]' : ''}`}>{r.days_remaining} days</span>
                  </td>
                  <td>
                    <div className="flex items-center gap-3">
                      <button onClick={() => restorePatient(r)} className="text-xs text-brand inline-flex items-center gap-1"
                              data-testid={`restore-patient-${r.user_id}`}>
                        <RotateCcw className="w-3.5 h-3.5"/> Restore
                      </button>
                      <button onClick={() => hardDeletePatient(r)} className="text-xs text-[#B83A3A] inline-flex items-center gap-1"
                              data-testid={`hard-delete-patient-${r.user_id}`}>
                        <Trash2 className="w-3.5 h-3.5"/> Erase now
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-fg-muted">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}
